import { useState, type ReactNode } from 'react';

import { api, qs } from '../api';
import {
  Aviso,
  Campo,
  Cartao,
  Estado,
  Etiqueta,
  Painel,
  Tempo,
  data,
  sistemaDe,
  useAcao,
  useCarga,
} from '../comum';
import type { Dispositivo } from '../tipos';
import { AbrirChamadoEmLote, Ficha } from './inventario/ficha';
import { TabelaDeDispositivos } from './inventario/tabela';
import {
  APTIDAO,
  CICLO,
  ROTULO_VERSAO,
  totalDaContagem,
  type ComViabilidade,
  type FiltroDeCartao,
  type Pagina,
  type RespostaDaPergunta,
  type Resumo,
} from './inventario/tipos';

/* `totalDaContagem` e os tipos de cartão seguem publicados por esta tela: é
   daqui que os testes e as outras abas os consomem. */
export { totalDaContagem };
export type { FiltroDeCartao, RespostaDaPergunta };

export function Inventario({
  aoPerderSessao,
  podeSincronizar,
  podeAbrirChamado,
  filtroDeCartao,
  aoLimparFiltro,
}: {
  aoPerderSessao: () => void;
  podeSincronizar: boolean;
  /** `decidir_sugestao` — exige `tickets: WRITE` no KACE. */
  podeAbrirChamado?: boolean;
  filtroDeCartao?: FiltroDeCartao | null;
  aoLimparFiltro?: () => void;
}): ReactNode {
  const [busca, setBusca] = useState('');
  const [familia, setFamilia] = useState('');
  const [versao, setVersao] = useState('');
  const [aptidao, setAptidao] = useState('');
  const [rotulo, setRotulo] = useState('');

  /**
   * Rótulos que o cliente já usa no KACE.
   *
   * Filtrar pelo rótulo dele é o que evita exigir que ele remodele o parque na
   * nossa linguagem antes de tirar valor do produto.
   */
  const rotulos = useCarga<{
    itens: readonly { nome: string; maquinas: number }[];
  }>(() => api.get('/api/rotulos'), [], aoPerderSessao);
  /**
   * Seleção em lote.
   *
   * Guardada por id, não por índice: a lista se recarrega sozinha e a posição
   * de uma máquina muda entre uma carga e outra.
   */
  const [selecao, setSelecao] = useState<ReadonlySet<string>>(new Set());
  const [abrindoChamado, setAbrindoChamado] = useState(false);

  /**
   * Pergunta em português, aqui mesmo.
   *
   * A capacidade já existia na aba Perguntar — mas quem está olhando máquinas
   * tinha de trocar de tela, perguntar, e voltar. O resultado de uma pergunta
   * sobre o parque **é uma lista de máquinas**: é exatamente onde a pessoa já
   * está.
   */
  const [pergunta, setPergunta] = useState('');
  const [perguntando, setPerguntando] = useState(false);
  const [pensando, setPensando] = useState(false);
  const [filtroDaPergunta, setFiltroDaPergunta] = useState<FiltroDeCartao | null>(null);

  const interpretar = (): void => {
    if (pergunta.trim() === '') return;
    setPensando(true);
    void acao
      .rodar(async () => {
        const r = await api.post<{
          explicacao: string;
          resposta?: string;
          consulta: {
            intencao: string;
            filtros: readonly unknown[];
            agruparPor: string | null;
          };
          resultado?: {
            readonly kind?: string;
            /** O caminho compilado devolve `intencao`, não `kind`. */
            readonly intencao?: string;
            readonly total?: number;
            readonly linhas?: readonly Record<string, unknown>[];
          };
        }>('/api/ia/perguntar', { pergunta });

        /**
         * 🔴 Pergunta sobre software não vem com filtro de dispositivo.
         *
         * Software mora em outra tabela e é respondido por caminho próprio, que
         * devolve `filtros: []`. Aplicar essa lista vazia mostrava **o parque
         * inteiro** — perguntar "máquinas com Office" trazia as 32, sem nada na
         * tela dizendo que aquilo não era o recorte pedido.
         *
         * A resposta já identificou as máquinas certas; convertemos os nomes num
         * filtro que o compilador entende. O recorte fica igual ao das outras
         * telas, em vez de existir um terceiro jeito de responder a mesma coisa.
         */
        const ehSoftware = r.resultado?.kind === 'software';
        const nomes = (r.resultado?.linhas ?? [])
          .map((l) => l['maquina'])
          .filter((n): n is string => typeof n === 'string' && n !== '');

        if (ehSoftware && nomes.length === 0) {
          throw new Error(`Nenhuma máquina do parque atende "${pergunta.trim()}".`);
        }

        const filtros = ehSoftware
          ? [{ campo: 'nome', operador: 'em', valor: nomes.slice(0, 400) }]
          : r.consulta.filtros;

        setFiltroDaPergunta({
          titulo: pergunta.trim(),
          explicacao: r.explicacao,
          // A tela lista; a pergunta podia estar contando. Ver `totalDaContagem`.
          total: totalDaContagem(r),
          frase: r.resposta ?? null,
          consulta: {
            intencao: 'listar',
            filtros,
            agruparPor: null,
            ordenarPor: null,
            ordemDescendente: false,
            limite: 400,
            explicacao: r.explicacao,
          },
        });
        return null;
      })
      .finally(() => setPensando(false));
  };
  const [aberto, setAberto] = useState<string | null>(null);
  const acao = useAcao(aoPerderSessao);

  const resumo = useCarga<Resumo>(() => api.get('/api/resumo'), [], aoPerderSessao);
  /**
   * Um filtro estruturado de cada vez, venha de onde vier.
   *
   * O cartão do painel manda um; a pergunta em português produz outro. Os dois
   * usam o mesmo caminho — a mesma rota, o mesmo compilador — então não há dois
   * jeitos de a tela mostrar uma lista filtrada.
   */
  const cartaoAtivo = filtroDeCartao ?? filtroDaPergunta;

  /**
   * Duas origens, uma tabela.
   *
   * Com filtro de cartão a lista vem do compilador — o **mesmo** que produziu o
   * número no painel. Se as duas rotas divergissem, o cliente veria "10" no
   * cartão e outra contagem ao clicar, e deixaria de confiar nas duas.
   */
  const lista = useCarga<Pagina>(
    () =>
      cartaoAtivo !== null
        ? api.post('/api/dispositivos/consulta', {
            consulta: cartaoAtivo.consulta,
            limite: 200,
          })
        : api.get(
            `/api/dispositivos${qs({
              busca,
              familia,
              versaoWindows: versao,
              viabilidade: aptidao,
              rotulo,
              limite: 200,
            })}`,
          ),
    [busca, familia, versao, aptidao, rotulo, JSON.stringify(cartaoAtivo?.consulta ?? null)],
    aoPerderSessao,
  );

  /**
   * Exporta o que está na tela.
   *
   * 🔴 O gestor gosta do número, abre a lista e não tem como tirar dali —
   * hoje ele printa a tela. Exportar **o recorte atual** (incluindo o filtro
   * que veio de um cartão) é o mínimo para o número virar reunião.
   *
   * Gerado no navegador, sem rota nova: os dados já estão aqui, e mandar o
   * servidor montar o mesmo CSV seria uma segunda fonte da verdade para
   * divergir da tabela que a pessoa está olhando.
   */
  const exportar = (): void => {
    const itens = lista.dados?.itens ?? [];
    if (itens.length === 0) return;

    const colunas: readonly [string, (d: Dispositivo) => string][] = [
      ['Nome', (d) => d.nome],
      ['Sistema', (d) => sistemaDe(d).texto],
      ['Fabricante', (d) => d.hwFabricante ?? ''],
      ['Modelo', (d) => d.hwModelo ?? ''],
      ['Usuário', (d) => d.usuarioLogado ?? ''],
      ['IP', (d) => d.ip ?? ''],
      ['Último inventário', (d) => (d.ultimoInventario ?? '').slice(0, 10)],
    ];

    // Ponto e vírgula e BOM: é o que o Excel em português abre sem perguntar
    // nada. CSV que abre torto é CSV que ninguém usa duas vezes.
    const escapar = (v: string): string => (/[;"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    const linhas = [
      colunas.map(([t]) => t).join(';'),
      ...itens.map((d) => colunas.map(([, f]) => escapar(f(d))).join(';')),
    ];

    const blob = new Blob(['\uFEFF' + linhas.join('\r\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const nome = cartaoAtivo?.titulo ?? 'inventario';
    a.download = `${nome.replace(/[^\w\- ]+/g, '').trim() || 'inventario'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sincronizar = (): void => {
    void acao.rodar(async () => {
      const r = await api.post<{
        dispositivos: { gravados: number };
        tickets: { gravados: number };
        duracaoMs: number;
      }>('/api/sync', { completo: false });
      resumo.recarregar();
      lista.recarregar();
      return `Sincronizado: ${r.dispositivos.gravados} dispositivo(s) e ${r.tickets.gravados} tíquete(s) em ${(r.duracaoMs / 1000).toFixed(1)}s.`;
    });
  };

  return (
    <>
      {acao.erro !== null && <Aviso tipo="erro">{acao.erro}</Aviso>}
      {acao.ok !== null && <Aviso tipo="ok">{acao.ok}</Aviso>}

      <Estado carga={resumo}>
        {resumo.dados !== null && (
          <>
            {!resumo.dados.kaceConfigurado && (
              <Aviso tipo="alerta">
                A conexão com o KACE não está configurada. Os dados abaixo são o que já havia no
                banco.
              </Aviso>
            )}
            {/*
              Os cartões deixaram de ser uma lista de contagens para virar uma
              lista de coisas que exigem ação. Windows 10 está fora de suporte
              desde outubro de 2025: é o número que vira projeto de upgrade, não
              mais um item ao lado de "macos 2".
            */}
            <div className="cartoes">
              <Cartao rotulo="Dispositivos" valor={resumo.dados.total} />
              <Cartao
                rotulo="Windows 10"
                valor={resumo.dados.distribuicao.find((d) => d.versao === 'win10')?.total ?? 0}
                nota="fora de suporte"
                tom="alerta"
                destaque
                titulo="Sem atualizações de segurança da Microsoft desde outubro de 2025."
              />
              <Cartao
                rotulo="Windows 11"
                valor={resumo.dados.distribuicao.find((d) => d.versao === 'win11')?.total ?? 0}
                nota="em suporte"
              />
              {/*
                A pergunta do ano, respondida na tela onde as máquinas estão.
                O produto já sabia calcular isso — só o Sentinela usava.
              */}
              {/*
                A lista de compra, em números.
                "Trocar" é o que vira orçamento; "renovar garantia" é o que
                evita trocar máquina que ainda serve.
              */}
              {resumo.dados.cicloDeVida !== undefined && resumo.dados.cicloDeVida.trocar > 0 && (
                <Cartao
                  rotulo="Trocar"
                  valor={resumo.dados.cicloDeVida.trocar}
                  nota="inaptas e fora de garantia"
                  tom="alerta"
                  destaque
                  titulo="Não suportam o Windows 11 e não têm cobertura. São candidatas diretas a substituição."
                />
              )}
              {resumo.dados.aptidaoWin11 !== undefined && (
                <>
                  <Cartao
                    rotulo="Aptas ao Windows 11"
                    valor={
                      resumo.dados.aptidaoWin11.apto + resumo.dados.aptidaoWin11.apto_com_ressalva
                    }
                    nota="dá para migrar sem trocar hardware"
                    tom="ok"
                    titulo="Atendem TPM 2.0, Secure Boot, memória e processador. Inclui as aptas com ressalva. Não conta as que já estão no Windows 11."
                  />
                  <Cartao
                    rotulo="Sem dados para decidir"
                    valor={resumo.dados.aptidaoWin11.indeterminado}
                    nota="falta coletar TPM ou Secure Boot"
                    titulo="Não são inaptas: ninguém mediu. Tratá-las como inaptas excluiria do projeto máquinas que talvez estejam prontas."
                  />
                </>
              )}
              <Cartao
                rotulo="Inventário parado"
                valor={resumo.dados.sinais.semInventarioRecente}
                nota="há mais de 30 dias"
                tom="alerta"
                destaque
                titulo="Agente parado, máquina desligada ou equipamento já aposentado sem baixa no KACE."
              />
              <Cartao
                rotulo="Sem agente"
                valor={resumo.dados.sinais.semAgente}
                nota="não reportam hardware"
                titulo="Máquina sem agente não reporta RAM nem CPU — o dado não está faltando por erro."
              />
            </div>
          </>
        )}
      </Estado>

      {/*
        Tarja do filtro que veio do cartão.
        A saída fica junto do que ela desfaz — filtro sem botão de limpar é
        armadilha: a pessoa vê uma lista curta e acha que o parque encolheu.
      */}
      {cartaoAtivo !== null && (
        <div className="tarja-filtro">
          <span>
            <strong>{filtroDeCartao !== null ? 'Filtro do cartão:' : 'Sua pergunta:'}</strong>{' '}
            {cartaoAtivo.titulo}
            {/*
              🔴 A resposta em português vem primeiro; a explicação técnica some
              para a linha de baixo.

              Antes, o que aparecia aqui era a descrição da consulta — e, quando
              a pergunta era de contagem, nem o número. Ver `consultas/redacao.ts`
              e `totalDaContagem`.
            */}
            {typeof cartaoAtivo.frase === 'string' && cartaoAtivo.frase !== '' ? (
              <div className="resposta-humana" style={{ marginTop: 6 }}>
                {cartaoAtivo.frase}
              </div>
            ) : (
              <>
                {typeof cartaoAtivo.total === 'number' && (
                  <>
                    {' '}
                    <strong className="resposta-contagem">{cartaoAtivo.total}</strong>
                  </>
                )}
                {cartaoAtivo.explicacao !== '' && (
                  <span className="suave"> — {cartaoAtivo.explicacao}</span>
                )}
              </>
            )}
            {typeof cartaoAtivo.frase === 'string' && cartaoAtivo.explicacao !== '' && (
              <div className="suave" style={{ fontSize: 13 }}>
                {cartaoAtivo.explicacao}
              </div>
            )}
          </span>
          <button
            className="link"
            onClick={() => {
              setFiltroDaPergunta(null);
              aoLimparFiltro?.();
            }}
          >
            limpar ✕
          </button>
        </div>
      )}

      {/*
        Perguntar em português, sem sair da tela.
        Fica antes dos filtros porque é o caminho mais curto para a maioria das
        perguntas — e porque quem não souber montar filtro consegue perguntar.
      */}
      {perguntando && (
        <div className="linha caixa-pergunta">
          <div>
            <input
              value={pergunta}
              placeholder="notebooks Dell com menos de 8 GB de memória"
              maxLength={300}
              autoFocus
              onChange={(e) => setPergunta(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') interpretar();
                if (e.key === 'Escape') setPerguntando(false);
              }}
            />
          </div>
          <button
            className="primario encolhe"
            disabled={pensando || pergunta.trim() === ''}
            onClick={interpretar}
          >
            {pensando ? 'Interpretando…' : 'Perguntar'}
          </button>
          <button className="encolhe" onClick={() => setPerguntando(false)}>
            Cancelar
          </button>
        </div>
      )}

      <div className="linha">
        <div>
          <input
            placeholder="Buscar por nome, IP, usuário, modelo…"
            disabled={cartaoAtivo !== null}
            title={
              cartaoAtivo === null ? undefined : 'Limpe o filtro do cartão para buscar livremente'
            }
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="encolhe">
          <select
            value={familia}
            disabled={cartaoAtivo !== null}
            onChange={(e) => setFamilia(e.target.value)}
          >
            <option value="">Todos os sistemas</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
            <option value="linux">Linux</option>
          </select>
        </div>
        <div className="encolhe">
          <select
            value={rotulo}
            disabled={cartaoAtivo !== null}
            title="Rótulos criados no KACE. Os ocultos do appliance ficam de fora."
            onChange={(e) => setRotulo(e.target.value)}
          >
            <option value="">Todos os rótulos</option>
            {(rotulos.dados?.itens ?? []).map((r) => (
              <option key={r.nome} value={r.nome}>
                {r.nome} ({r.maquinas})
              </option>
            ))}
          </select>
        </div>
        <div className="encolhe">
          <select
            value={aptidao}
            disabled={cartaoAtivo !== null}
            title="Aptidão para Windows 11, calculada a partir de TPM, Secure Boot, build, memória e processador"
            onChange={(e) => setAptidao(e.target.value)}
          >
            <option value="">Windows 11: qualquer aptidão</option>
            <option value="ja_atende">Já no Windows 11</option>
            <option value="apto">Aptas</option>
            <option value="apto_com_ressalva">Aptas com ressalva</option>
            <option value="inapto">Inaptas</option>
            <option value="indeterminado">Sem dados para decidir</option>
          </select>
        </div>
        <div className="encolhe">
          <select
            value={versao}
            disabled={cartaoAtivo !== null}
            onChange={(e) => setVersao(e.target.value)}
          >
            <option value="">Todas as versões</option>
            {Object.entries(ROTULO_VERSAO).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        {!perguntando && (
          <button
            className="encolhe"
            title="Descreva o que procura em português — a IA monta o filtro"
            onClick={() => setPerguntando(true)}
          >
            ✦ Perguntar
          </button>
        )}
        {podeSincronizar && (
          <>
            <button className="primario encolhe" disabled={acao.executando} onClick={sincronizar}>
              {acao.executando ? 'Sincronizando…' : 'Sincronizar'}
            </button>
            {/*
              Sync completo.
              🔴 Necessário quando o produto passa a coletar um campo novo: o
              incremental só traz quem mudou no KACE, e quem não mudou fica sem
              o dado indefinidamente. Ao acrescentar rótulos, o primeiro sync
              trouxe 1 máquina de 32.
            */}
            <button
              className="encolhe"
              disabled={acao.executando}
              title="Rebusca o parque inteiro, ignorando o que já foi sincronizado. Use depois de uma atualização que traga campos novos."
              onClick={() =>
                void acao.rodar(async () => {
                  const r = await api.post<{
                    dispositivos: { gravados: number };
                  }>('/api/sync', { completo: true });
                  resumo.recarregar();
                  lista.recarregar();
                  return `Sincronização completa: ${r.dispositivos.gravados} dispositivo(s) atualizados.`;
                })
              }
            >
              Sincronizar tudo
            </button>
          </>
        )}
      </div>

      {/*
        Ação em lote.
        🔴 Isto CRIA um chamado real no Service Desk do cliente. A barra só
        aparece com seleção e com permissão, e o servidor exige confirmação
        explícita — a trava está onde a escrita acontece, não só aqui.
      */}
      {selecao.size > 0 && podeAbrirChamado === true && (
        <div className="barra-lote">
          <span>
            <strong>{selecao.size}</strong> máquina(s) selecionada(s)
          </span>
          <span className="cresce" />
          <button className="link" onClick={() => setSelecao(new Set())}>
            limpar seleção
          </button>
          <button className="primario" onClick={() => setAbrindoChamado(true)}>
            Abrir chamado com estas máquinas
          </button>
        </div>
      )}

      {abrindoChamado && (
        <AbrirChamadoEmLote
          dispositivoIds={[...selecao]}
          aoPerderSessao={aoPerderSessao}
          aoFechar={() => setAbrindoChamado(false)}
          aoAbrir={() => {
            setAbrindoChamado(false);
            setSelecao(new Set());
          }}
        />
      )}

      <TabelaDeDispositivos
        lista={lista}
        selecao={selecao}
        setSelecao={setSelecao}
        aoAbrir={setAberto}
        exportar={exportar}
        podeSelecionar={podeAbrirChamado === true}
      />

      {aberto !== null && (
        <Ficha id={aberto} aoFechar={() => setAberto(null)} aoPerderSessao={aoPerderSessao} />
      )}
    </>
  );
}
